/**
 * Descriptors for the More ▸ DBA group.
 *
 * Split out of `toolbarMoreItems.ts` so both stay under the 200-line
 * guideline; the group is also the only one whose whole gate can close
 * (`hasDbaEligibleConnection`), which makes it a natural seam.
 *
 * Each `visible` predicate is the "is ANY connected connection eligible"
 * check INJ-2 introduced — never the tree selection. Each mirrors the
 * `engineFilter` its own action passes to `requestContext`, so a visible row
 * can never resolve a connection its action would reject.
 */
import { Activity, Bot, DatabaseBackup, HardDrive, History, Package, Users } from 'lucide-react';
import type { ToolbarItemDescriptor, ToolbarModelDeps } from './toolbarItemTypes';

export function buildDbaToolbarItems({ gating, actions }: ToolbarModelDeps): ToolbarItemDescriptor[] {
  return [
    {
      id: 'session-browser',
      render: 'menu-item',
      label: 'Session Browser',
      testId: 'session-browser-menu-item',
      icon: Users,
      commandId: 'workspace:session-browser',
      run: actions['session-browser'],
    },
    {
      id: 'tablespace-manager',
      render: 'menu-item',
      label: 'Tablespace Manager',
      testId: 'tablespace-manager-menu-item',
      icon: HardDrive,
      visible: gating.hasAnyOracleConnection,
      commandId: 'workspace:tablespace-manager',
      run: actions['tablespace-manager'],
    },
    {
      id: 'package-explorer',
      render: 'menu-item',
      label: 'Package Explorer',
      testId: 'package-explorer-menu-item',
      icon: Package,
      visible: gating.hasAnyOracleConnection && gating.betaEnabled['package-explorer'],
      commandId: null, // no palette/menu-bar equivalent today
      run: actions['package-explorer'],
    },
    {
      id: 'filegroup-manager',
      render: 'menu-item',
      label: 'Filegroup Manager',
      testId: 'filegroup-manager-menu-item',
      icon: HardDrive,
      visible: gating.hasAnyMssqlConnection,
      commandId: 'workspace:filegroup-manager',
      run: actions['filegroup-manager'],
    },
    {
      id: 'query-store',
      render: 'menu-item',
      label: 'Query Store...',
      testId: 'query-store-menu-item',
      icon: History,
      visible: gating.hasAnyMssqlConnection,
      commandId: 'workspace:query-store',
      run: actions['query-store'],
    },
    {
      id: 'agent-jobs',
      render: 'menu-item',
      // Engine-specific wording ("Agent Jobs" / "Scheduler Jobs" / …).
      label: gating.jobSchedulerMenuLabel,
      testId: 'agent-jobs-menu-item',
      icon: Bot,
      visible: gating.hasJobScheduler && gating.betaEnabled['job-scheduler'],
      commandId: 'workspace:job-scheduler',
      run: actions['agent-jobs'],
    },
    {
      id: 'instance-monitor',
      render: 'menu-item',
      label: 'Instance Monitor',
      testId: 'instance-monitor-menu-item',
      icon: Activity,
      commandId: 'workspace:instance-monitor',
      run: actions['instance-monitor'],
    },
    {
      id: 'data-pump',
      render: 'menu-item',
      label: 'Data Pump',
      testId: 'data-pump-menu-item',
      icon: DatabaseBackup,
      visible: gating.hasAnyOracleConnection,
      commandId: 'workspace:data-pump',
      run: actions['data-pump'],
    },
  ];
}
